import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Plus, Boat, AirplaneTilt, Truck, Stack, MagnifyingGlass } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const EMPTY = {
  shipment_method: "sea",
  carrier: "",
  voyage_or_flight: "",
  origin: "",
  destination: "",
  departure_date: "",
  arrival_date: "",
  notes: "",
  company_id: "",
};

const STATUS_LABELS = {
  draft: "Draft",
  sealed: "Sealed",
  in_transit: "In Transit",
  arrived: "Arrived",
};

const STATUS_STYLES = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
  sealed: "bg-sky-50 text-sky-700 border-sky-200",
  in_transit: "bg-amber-50 text-amber-700 border-amber-200",
  arrived: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Manifests() {
  const { user } = useAuth();
  // Platform-level roles (super_admin + every Kanal warehouse-super level) see + create across all companies.
  const isPlatform =
    user.role === "super_admin" ||
    user.role === "warehouse_super" ||
    user.role === "warehouse_super_l1" ||
    user.role === "warehouse_super_l2";
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (methodFilter !== "all") params.shipment_method = methodFilter;
    if (search.trim()) params.q = search.trim();
    const { data } = await api.get("/manifests", { params });
    setRows(data);
    if (isPlatform && companies.length === 0) {
      const c = await api.get("/companies");
      setCompanies(c.data);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, methodFilter, search]);

  const save = async () => {
    try {
      const payload = { ...form };
      if (!isPlatform) delete payload.company_id;
      else if (!payload.company_id) { toast.error("Please select a company"); return; }
      // Send only filled fields
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/manifests", payload);
      toast.success("Manifest created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Manifest creation: super_admin, company_admin, dispatcher, and Level-2
  // Kanal Super Warehouse. L1 warehouse (super or legacy) can only VIEW /
  // ADD PACKAGES — not spin up new manifests. Mirrors the backend guard.
  const canManage =
    user.role === "super_admin" ||
    user.role === "company_admin" ||
    user.role === "dispatcher" ||
    user.role === "warehouse_super" ||
    user.role === "warehouse_super_l2";

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="manifests-page">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Operations</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
            Shipment Manifests
          </h1>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="add-manifest-btn">
                <Plus size={16} className="mr-1.5" weight="bold" /> New Manifest
              </Button>
            </DialogTrigger>
            <DialogContent
              className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
              data-testid="manifest-dialog"
            >
              <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
                <DialogTitle>Create Manifest</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 overflow-y-auto px-5 sm:px-6 py-4 flex-1" data-testid="manifest-dialog-body">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Shipment method</Label>
                  <Select value={form.shipment_method} onValueChange={(v) => setForm({ ...form, shipment_method: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="manifest-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="air" data-testid="manifest-method-air">Air Freight</SelectItem>
                      <SelectItem value="sea" data-testid="manifest-method-sea">Ocean Freight</SelectItem>
                      <SelectItem value="ground" data-testid="manifest-method-ground">Ground</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Carrier</Label>
                    <Input className="mt-1.5" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} data-testid="manifest-carrier" placeholder={form.shipment_method === "sea" ? "e.g. Tropical Shipping" : form.shipment_method === "ground" ? "e.g. Knutsford Express" : "e.g. Caribbean Airlines"} />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{form.shipment_method === "sea" ? "Voyage" : form.shipment_method === "ground" ? "Trip" : "Flight"}</Label>
                    <Input className="mt-1.5" value={form.voyage_or_flight} onChange={(e) => setForm({ ...form, voyage_or_flight: e.target.value })} data-testid="manifest-voyage" placeholder={form.shipment_method === "sea" ? "V-2026-014" : form.shipment_method === "ground" ? "GR-2026-014" : "BW-022"} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Origin</Label>
                  <div className="mt-1.5">
                    <AddressAutocomplete value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} dataTestId="manifest-origin" rows={1} placeholder="e.g. Miami, FL" country={undefined} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Destination</Label>
                  <div className="mt-1.5">
                    <AddressAutocomplete value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} dataTestId="manifest-destination" rows={1} placeholder="e.g. Kingston, JM" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Departure</Label>
                    <Input type="date" className="mt-1.5" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} data-testid="manifest-departure" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Arrival</Label>
                    <Input type="date" className="mt-1.5" value={form.arrival_date} onChange={(e) => setForm({ ...form, arrival_date: e.target.value })} data-testid="manifest-arrival" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Notes</Label>
                  <Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="manifest-notes" />
                </div>
                {isPlatform && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="manifest-company"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-manifest-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="manifest-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sealed">Sealed</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="arrived">Arrived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-44" data-testid="manifest-method-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All methods</SelectItem>
            <SelectItem value="air">Air Freight</SelectItem>
            <SelectItem value="sea">Ocean Freight</SelectItem>
            <SelectItem value="ground">Ground</SelectItem>
          </SelectContent>
        </Select>
        <form
          onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); }}
          className="relative flex-1 max-w-sm"
        >
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={() => setSearch(searchInput)}
            placeholder="Search by manifest # (e.g. MAN-2026-001)"
            className="pl-8 h-9"
            data-testid="manifest-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearchInput(""); setSearch(""); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-500 hover:text-zinc-800"
              data-testid="manifest-search-clear"
            >
              clear
            </button>
          )}
        </form>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="manifests-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Manifest #</th>
              <th className="px-5 py-3 font-semibold">Method</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Route</th>
              <th className="px-5 py-3 font-semibold">Packages</th>
              <th className="px-5 py-3 font-semibold">Weight</th>
              <th className="px-5 py-3 font-semibold">Departure</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-5 py-12 text-center text-zinc-500">
                <Stack size={28} className="mx-auto mb-2 text-zinc-300" />
                No manifests yet. Group Air or Sea packages into a manifest to track them as a single shipment.
              </td></tr>
            ) : rows.map((m) => (
              <tr key={m.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`manifest-row-${m.id}`}>
                <td className="px-5 py-4">
                  <code className="text-xs font-mono font-semibold text-zinc-900 bg-zinc-100 px-1.5 py-0.5 rounded">{m.manifest_number}</code>
                </td>
                <td className="px-5 py-4">
                  {m.shipment_method === "sea" ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200">
                      <Boat size={11} weight="bold" /> Sea
                    </span>
                  ) : m.shipment_method === "ground" ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Truck size={11} weight="bold" /> Ground
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                      <AirplaneTilt size={11} weight="bold" /> Air
                    </span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold border ${STATUS_STYLES[m.status] || STATUS_STYLES.draft}`} data-testid={`manifest-status-${m.id}`}>
                    {STATUS_LABELS[m.status] || m.status}
                  </span>
                </td>
                <td className="px-5 py-4 text-xs text-zinc-700">
                  <div>{m.origin || "—"}</div>
                  <div className="text-zinc-400">→ {m.destination || "—"}</div>
                </td>
                <td className="px-5 py-4 font-semibold text-zinc-900">{m.package_count}</td>
                <td className="px-5 py-4 text-zinc-700">
                  <div>{m.total_weight_kg?.toFixed(2)} kg</div>
                  <div className="text-[11px] text-zinc-500">{((m.total_weight_kg || 0) * 2.20462).toFixed(2)} lb</div>
                </td>
                <td className="px-5 py-4 text-zinc-700 text-xs">{m.departure_date ? new Date(m.departure_date).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-4 text-right">
                  <Link to={`/manifests/${m.id}`} className="text-xs font-semibold text-[#002BF6] hover:text-[#0021C7]" data-testid={`view-manifest-${m.id}`}>
                    Open 
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
